// The signalling socket of a call (/api/voice): JSON both ways, and what is
// sent before it opens is kept until it does. It does not connect again by
// itself; the session decides (it resumes after a broken way).
import {
  VOICE_PATH,
  type VoiceClientMessage,
  type VoiceServerMessage,
} from "@trameria/cloud-api/voice-shared";

export interface VoiceSignal {
  send(msg: VoiceClientMessage): void;
  /**
   * Closes it on purpose (no onClose). 1000 (the default) leaves the call;
   * `VOICE_REJOIN_CLOSE` keeps the stay for the socket opened next.
   */
  close(code?: number): void;
}

export type ConnectSignal = (opts: {
  slug: string;
  roomId: string;
  /** The socket id of the stay to take over (after the way broke). */
  resume: string | null;
  onMessage(msg: VoiceServerMessage): void;
  /** Closed by the server or a broken way (never after `close()`). */
  onClose(code: number): void;
}) => VoiceSignal;

export function signalUrl(
  slug: string,
  roomId: string,
  resume: string | null,
): string {
  const scheme = location.protocol === "https:" ? "wss" : "ws";
  const q = new URLSearchParams({ workspace: slug, room: roomId });
  if (resume) q.set("resume", resume);
  return `${scheme}://${location.host}${VOICE_PATH}?${q}`;
}

export const connectSignal: ConnectSignal = (opts) => {
  const socket = new WebSocket(signalUrl(opts.slug, opts.roomId, opts.resume));
  let opened = false;
  let closedByMe = false;
  const pending: string[] = [];
  socket.onopen = () => {
    opened = true;
    for (const s of pending.splice(0)) socket.send(s);
  };
  socket.onmessage = (ev: MessageEvent<string>) => {
    try {
      opts.onMessage(JSON.parse(ev.data) as VoiceServerMessage);
    } catch (error) {
      console.error("voice: bad signal", error);
    }
  };
  socket.onclose = (ev: CloseEvent) => {
    if (!closedByMe) opts.onClose(ev.code);
  };
  return {
    send(msg) {
      const s = JSON.stringify(msg);
      if (opened && socket.readyState === WebSocket.OPEN) socket.send(s);
      else if (!opened) pending.push(s);
    },
    close(code = 1000) {
      closedByMe = true;
      socket.close(code);
    },
  };
};
